import { useCallback, useEffect, useMemo, useState } from 'react'
import { NavLink, Route, Routes, useNavigate } from 'react-router-dom'
import { AlertTriangle, IdCard, Users, UserPlus } from 'lucide-react'
import { cn } from '@/lib/cn'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { useAuth } from '@/providers/AuthProvider'
import { useToast } from '@/providers/UiProviders'
import { PERMISSIONS, isSuperAdmin } from '@/lib/permissions'
import { PermissionDenied, ErrorState, Skeleton } from '@/components/States'
import { StatusBadge, Tag } from '@/components/StatusBadge'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { ApiError } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import { Input, Select, Field } from '@/components/ui/input'
import {
  fetchStaff,
  fetchRoleCatalog,
  setStaffRole,
  createStaff,
  type AdminStaff,
  type AdminRoleDef,
} from '@/lib/adapters'
import { StaffCard } from './StaffCard'

/**
 * A23 — Xodimlar va tashkilot (18 ekran).
 *
 * HOLAT (P1 rol-modeli JONLI, backend `63c2342`):
 *   A23.01 Xodimlar ro'yxati   — JONLI (GET /admin/staff + superadmin rol-tahrir)
 *   A23.03 Rollar boshqaruvi   — klient katalog (server /admin/roles bilan mos)
 *   A23.04 Huquqlar matritsasi — ISHLAYDI (klient katalogidan)
 *   qolgan 15 ekran            — huquq-berish/JIT/break-glass = keyingi bosqich
 */

function Tabs() {
  const t = useT()
  const tabs = [
    { to: '/staff', end: true, label: t.staff.title, id: 'A23.01' },
    { to: '/staff/roles', end: false, label: t.staff.roles, id: 'A23.03' },
    { to: '/staff/permissions', end: false, label: t.staff.permissionMatrix, id: 'A23.04' },
  ]
  return (
    <div className="flex items-center gap-1 border-b border-border-subtle px-3">
      {tabs.map((tab) => (
        <NavLink
          key={tab.to}
          to={tab.to}
          end={tab.end}
          className={({ isActive }) =>
            cn(
              'flex items-center gap-1.5 border-b-2 px-2.5 py-2 text-base transition-colors',
              isActive
                ? 'border-accent font-medium text-primary'
                : 'border-transparent text-secondary hover:text-primary',
            )
          }
        >
          {tab.label}
          <span className="font-mono text-xs text-tertiary">{tab.id}</span>
        </NavLink>
      ))}
    </div>
  )
}

/* ── A23.01 Xodimlar ro'yxati — JONLI (GET /admin/staff, backend `63c2342`) ──
   Superadmin har xodimning rolini o'zgartira oladi (POST /admin/staff/:id/role;
   server superadmin-gated + oxirgi-superadmin qulfi + audit). */

function StaffList({ reloadKey = 0 }: { reloadKey?: number }) {
  const t = useT()
  const navigate = useNavigate()
  const { identity } = useAuth()
  const { toast } = useToast()
  const isSuper = isSuperAdmin(identity)

  const rowActions = useMemo<RowAction<AdminStaff>[]>(
    () => [
      {
        id: 'card',
        label: t.users.viewCard,
        icon: <IdCard size={13} />,
        onSelect: (r) => navigate(`/staff/member/${r.id}`),
      },
    ],
    [t, navigate],
  )
  const [overrides, setOverrides] = useState<Record<number, string>>({})
  const [assignable, setAssignable] = useState<string[]>([])

  useEffect(() => {
    const ac = new AbortController()
    fetchRoleCatalog(ac.signal)
      .then((roles) => setAssignable(roles.filter((r) => r.assignable).map((r) => r.role)))
      .catch(() => {})
    return () => ac.abort()
  }, [])

  const changeRole = async (id: number, role: string) => {
    const prev = overrides[id]
    setOverrides((o) => ({ ...o, [id]: role }))
    try {
      await setStaffRole(id, role)
      toast(t.staff.roleSaved, { tone: 'success' })
    } catch (e) {
      setOverrides((o) => ({ ...o, [id]: prev ?? '' }))
      toast(t.staff.roleFailed, {
        tone: 'error',
        ...(e instanceof Error && e.message ? { description: e.message } : {}),
      })
    }
  }

  const columns = useMemo<AdminColumn<AdminStaff>[]>(
    () => [
      { key: 'id', header: t.col.id, type: 'id', width: 70 },
      { key: 'username', header: t.account.username, type: 'text', minWidth: 150 },
      {
        key: 'role',
        header: t.account.role,
        type: 'text',
        minWidth: 180,
        sortable: false,
        render: (r) => {
          const role = overrides[r.id] ?? r.role
          if (!isSuper) return <span className="font-mono text-sm">{role}</span>
          const opts = assignable.includes(role) ? assignable : [role, ...assignable]
          return (
            <select
              value={role}
              onChange={(e) => changeRole(r.id, e.target.value)}
              className="rounded-md border border-border-default bg-surface-1 px-2 py-1 font-mono text-xs text-primary"
            >
              {opts.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          )
        },
      },
      {
        key: 'is_active',
        header: t.col.status,
        type: 'enum',
        width: 120,
        accessor: (r) => (r.is_active ? 'active' : 'inactive'),
        options: [
          { value: 'active', label: t.status.active, status: 'active' },
          { value: 'inactive', label: t.staff.inactive, status: 'deleted' },
        ],
      },
      { key: 'permission_count', header: t.staff.permCount, type: 'number', width: 110, align: 'right' },
      { key: 'last_login_at', header: t.staff.lastLogin, type: 'date', width: 160 },
      { key: 'created_at', header: t.col.registered, type: 'date', width: 160, hiddenByDefault: true },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, isSuper, assignable, overrides],
  )

  const fetcher = useCallback(fetchStaff, [])

  return (
    <DataTable<AdminStaff>
      key={reloadKey}
      screenId="A23.01"
      permission={PERMISSIONS.STAFF_VIEW}
      columns={columns}
      fetcher={fetcher}
      rowId={(r) => String(r.id)}
      rowActions={rowActions}
      exportable
      emptyState={{ icon: <Users size={28} strokeWidth={1.5} /> }}
    />
  )
}

/* ── A23.03 Rollar boshqaruvi ─────────────────────────────────────
   JONLI server rol-katalogidan (GET /admin/roles → fetchRoleCatalog): rol
   RO'YXATI + har rol huquq soni. `all` = super_admin (barcha huquq); huquqlar
   umumiy soni serverdagi barcha rollar huquqlarining birlashmasidan hisoblanadi.
   Yozish (A23.05) = RoleEditorScreen (POST /admin/roles). */

function RoleList() {
  const t = useT()
  const [roles, setRoles] = useState<AdminRoleDef[] | null>(null)
  const [err, setErr] = useState<ApiError | null>(null)
  const [tick, setTick] = useState(0)
  const reload = useCallback(() => setTick((x) => x + 1), [])

  useEffect(() => {
    const ac = new AbortController()
    setErr(null)
    fetchRoleCatalog(ac.signal)
      .then(setRoles)
      .catch((e) => {
        if (!ac.signal.aborted)
          setErr(e instanceof ApiError ? e : new ApiError('INTERNAL', String(e), 500))
      })
    return () => ac.abort()
  }, [tick])

  // Umumiy huquqlar soni — barcha rollar permissions[] birlashmasi ('*' chiqarib).
  const total = useMemo(() => {
    const set = new Set<string>()
    for (const r of roles ?? []) for (const p of r.permissions) if (p !== '*') set.add(p)
    return set.size
  }, [roles])

  return (
    <div className="min-h-0 flex-1 overflow-auto p-3">
      <div className="mb-3 flex items-start gap-2 rounded-md border border-warning bg-warning-subtle p-2.5">
        <AlertTriangle size={14} className="mt-0.5 shrink-0 text-warning" />
        <p className="text-sm text-secondary">{t.staff.rolesNote}</p>
      </div>
      {err ? (
        <ErrorState code={err.code} onRetry={reload} />
      ) : !roles ? (
        <div className="space-y-1.5">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-11 w-full" />
          ))}
        </div>
      ) : (
        <div className="overflow-hidden rounded-md border border-border-default">
          {roles.map((r, i) => {
            const defined = r.all || r.permission_count > 0
            return (
              <div
                key={r.role}
                className={cn(
                  'flex items-center gap-3 px-3 py-2.5',
                  i > 0 && 'border-t border-border-subtle',
                )}
              >
                <span className="w-56 shrink-0 font-mono text-sm text-primary">{r.role}</span>
                <span className="min-w-0 flex-1 font-mono text-sm text-secondary tabular">
                  {r.all ? t.staff.allPerms : `${r.permission_count}/${total}`}
                </span>
                {defined ? (
                  <StatusBadge status="active" label={t.modules.live} />
                ) : (
                  <span className="shrink-0 text-xs text-tertiary">{t.modules.planned}</span>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

/* ── A23.04 Huquqlar matritsasi ───────────────────────────────── */

function PermissionMatrix() {
  const t = useT()
  const [roles, setRoles] = useState<AdminRoleDef[] | null>(null)
  const [err, setErr] = useState<ApiError | null>(null)
  const [tick, setTick] = useState(0)
  const [onlyDefined, setOnlyDefined] = useState(true)
  const reload = useCallback(() => setTick((x) => x + 1), [])

  useEffect(() => {
    const ac = new AbortController()
    setErr(null)
    fetchRoleCatalog(ac.signal)
      .then(setRoles)
      .catch((e) => {
        if (!ac.signal.aborted)
          setErr(e instanceof ApiError ? e : new ApiError('INTERNAL', String(e), 500))
      })
    return () => ac.abort()
  }, [tick])

  // Ustunlar — serverdagi barcha rollar huquqlarining birlashmasi ('*' chiqarib).
  const allPerms = useMemo(() => {
    const set = new Set<string>()
    for (const r of roles ?? []) for (const p of r.permissions) if (p !== '*') set.add(p)
    return [...set].sort()
  }, [roles])
  const definedRoles = useMemo(
    () => (roles ?? []).filter((r) => r.all || r.permission_count > 0),
    [roles],
  )
  const shown = onlyDefined ? definedRoles : (roles ?? [])

  if (err) {
    return (
      <div className="p-3">
        <ErrorState code={err.code} onRetry={reload} />
      </div>
    )
  }
  if (!roles) {
    return (
      <div className="space-y-1.5 p-3">
        {Array.from({ length: 8 }).map((_, i) => (
          <Skeleton key={i} className="h-9 w-full" />
        ))}
      </div>
    )
  }

  return (
    <div className="min-h-0 flex-1 overflow-auto p-3">
      <div className="mb-3 flex items-start gap-2 rounded-md border border-warning bg-warning-subtle p-2.5">
        <AlertTriangle size={14} className="mt-0.5 shrink-0 text-warning" />
        <div className="text-sm">
          <p className="font-medium text-warning">
            {definedRoles.length}/{roles.length} rol · {allPerms.length} huquq (server katalogi)
          </p>
          <p className="mt-0.5 text-secondary">
            Bu matritsa JONLI server rol-katalogidan (
            <span className="font-mono text-xs">GET /admin/roles</span>) o'qiladi. Haqiqiy
            majburlash serverda (<span className="font-mono text-xs">RequirePermission</span>).
          </p>
        </div>
      </div>

      <label className="mb-2 flex items-center gap-2 text-sm text-secondary">
        <input
          type="checkbox"
          checked={onlyDefined}
          onChange={(e) => setOnlyDefined(e.target.checked)}
          className="h-3.5 w-3.5 accent-[var(--accent)]"
        />
        Faqat aniqlangan rollar
      </label>

      <div className="overflow-auto rounded-md border border-border-default">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="bg-surface-2">
              <th className="sticky left-0 z-10 border-b border-border-default bg-surface-2 px-2.5 py-2 text-left text-xs font-medium tracking-wide text-tertiary uppercase">
                {t.staff.role}
              </th>
              {allPerms.map((p) => (
                <th
                  key={p}
                  className="border-b border-l border-border-subtle px-1.5 py-2 text-center align-bottom"
                  title={p}
                >
                  <span
                    className="inline-block font-mono text-xs whitespace-nowrap text-tertiary"
                    style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
                  >
                    {p}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => (
              <tr key={r.role} className="hover:bg-surface-3">
                <td className="sticky left-0 z-10 border-b border-border-subtle bg-surface-1 px-2.5 py-1.5">
                  <div className="flex items-center gap-2 whitespace-nowrap">
                    <span className="font-mono text-sm text-primary">{r.role}</span>
                    {r.all && <Tag>*</Tag>}
                  </div>
                </td>
                {allPerms.map((p) => {
                  const on = r.all || r.permissions.includes(p)
                  return (
                    <td
                      key={p}
                      className="border-b border-l border-border-subtle px-1.5 py-1.5 text-center"
                      title={`${r.role} · ${p}`}
                    >
                      {on ? (
                        <span className="text-success">✓</span>
                      ) : (
                        <span className="text-disabled">·</span>
                      )}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-3 flex items-center gap-4 text-xs text-tertiary">
        <span className="flex items-center gap-1">
          <span className="text-success">✓</span> ruxsat
        </span>
        <span className="flex items-center gap-1">
          <span className="text-disabled">·</span> yo'q
        </span>
      </div>
    </div>
  )
}

/* ── Kirish nuqtasi ───────────────────────────────────────────── */

export function StaffScreen() {
  const t = useT()
  const { can, identity } = useAuth()
  const [showCreate, setShowCreate] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)
  const canCreate = isSuperAdmin(identity) || can(PERMISSIONS.STAFF_CREATE)

  if (!can(PERMISSIONS.STAFF_VIEW)) return <PermissionDenied permission={PERMISSIONS.STAFF_VIEW} />

  return (
    <>
      <PageHeader
        title={t.staff.title}
        screenId="A23"
        description={t.staff.subtitle}
        actions={
          <div className="flex items-center gap-2">
            <StatusBadge status="review" label="Faza 1" />
            {canCreate && (
              <Button variant="primary" size="sm" onClick={() => setShowCreate(true)}>
                <UserPlus size={14} />
                Xodim qo‘shish
              </Button>
            )}
          </div>
        }
      />
      <Tabs />
      <div className="flex min-h-0 flex-1 flex-col">
        <Routes>
          <Route index element={<StaffList reloadKey={reloadKey} />} />
          <Route path="member/:id" element={<StaffCard />} />
          <Route path="roles" element={<RoleList />} />
          <Route path="permissions" element={<PermissionMatrix />} />
        </Routes>
      </div>
      {showCreate && (
        <CreateStaffDialog
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false)
            setReloadKey((k) => k + 1)
          }}
        />
      )}
    </>
  )
}

/* ── Yangi xodim yaratish (login + parol + rol) — superadmin ────────
   Backend: POST /admin/staff (RequireAdminRole("superadmin")). Rol JONLI
   katalogdan (assignable) tanlanadi. */
function CreateStaffDialog({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const { toast } = useToast()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [role, setRole] = useState('')
  const [roles, setRoles] = useState<string[]>([])
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    const ac = new AbortController()
    fetchRoleCatalog(ac.signal)
      .then((rs) => {
        const a = rs.filter((r) => r.assignable).map((r) => r.role)
        setRoles(a)
        // Default to the first non-superadmin role (safer default), else the first.
        const first = a.find((r) => r !== 'superadmin') ?? a[0]
        if (first) setRole(first)
      })
      .catch(() => {})
    return () => ac.abort()
  }, [])

  const valid = username.trim().length >= 3 && password.length >= 8 && !!role

  const submit = async () => {
    if (!valid || saving) return
    setSaving(true)
    setErr(null)
    try {
      await createStaff(username.trim(), password, role)
      toast('Xodim yaratildi', { tone: 'success' })
      onCreated()
    } catch (e) {
      setErr(e instanceof Error && e.message ? e.message : 'Xatolik yuz berdi')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title="Yangi xodim"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose}>
            Bekor
          </Button>
          <Button variant="primary" size="sm" onClick={submit} disabled={!valid || saving}>
            {saving ? 'Yaratilmoqda…' : 'Yaratish'}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        <Field label="Login" required htmlFor="cs-user">
          <Input
            id="cs-user"
            value={username}
            autoFocus
            autoComplete="off"
            placeholder="masalan: operator1"
            onChange={(e) => setUsername(e.target.value)}
          />
        </Field>
        <Field label="Parol" required htmlFor="cs-pass" hint="Kamida 8 belgi">
          <Input
            id="cs-pass"
            type="password"
            value={password}
            autoComplete="new-password"
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>
        <Field label="Rol" required htmlFor="cs-role">
          <Select id="cs-role" value={role} onChange={(e) => setRole(e.target.value)}>
            {roles.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </Select>
        </Field>
        {err && <div className="text-sm text-danger">{err}</div>}
      </div>
    </Dialog>
  )
}
